import React, {
    useEffect, useState, useMemo, useCallback, Dispatch, SetStateAction,
} from 'react';
import { faArrowRight, faAngleRight, faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isEmpty } from 'lodash';
import { POPULATE_USES_METHOD, VIEW_ENTITY } from 'views/ProFormaTable/constants';
import { Col, Row, UncontrolledTooltip } from 'reactstrap';
import RenderIf from 'components/RenderIf/RenderIf';
import { NSButton, NSCard } from 'bricks';
import { IProFormaLocalStorage, IProFormaPageLocalStorage, IUnitTypeOption, UsesView } from 'views/ProFormaTable/types';
import { useGetDealById } from 'views/Deals/hooks/useGetDealById';
import useDebounce from 'hooks/useDebounce';
import { IOptionAnyValue } from 'bricks/types';
import { useAuthContext } from 'contexts/AuthContext';
import { RECOMMENDED_CATEGORIES_EXPANDED_STATE, RECOMMENDED_CATEGORIES_PREVIEW_DATA } from 'views/ProFormaTable/recommendedCategoriesConstants';
import { useGetDealsWithProFormas, useOrganizationSubprojects } from 'views/ProFormaTable/hooks';
import { PRO_FORMA_STATUS } from 'views/ProFormaHome/constants';
import { useGetBackOfEnvelopeDeals } from 'views/DealDashboard/BOE/hooks/useGetBOEDeals';
import { PageSpecificKeys, useLocalStorage } from 'helpers/localStorage';
import { useGetBackOfEnvelopeScenarios } from 'views/DealDashboard/BOE/hooks/useGetBOEScenarios';
import DealScenarioDropdown from 'components/DealScenarioDropdown/DealScenarioDropdown';
import DevelopmentUsesTable from '../DevelopmentUsesTable/DevelopmentUsesTable';
import { UsesTableProvider } from '../DevelopmentUsesTable/context/UsesTableProvider';
import '../ProFormaDevelopment.scss';
import ImportSearchBar from '../ImportSearchBar/ImportSearchBar';
import ImportSourceList from '../ImportSourceList/ImportSourceList';
import PreviewImportSection from '../PreviewImportSection/PreviewImportSection';
import { useProFormaSocketContext } from '../../socketContext/ProFormaSocketProvider';
import UsesCardsViewToggle from '../UsesCardsOverview/UsesCardsViewToggle/UsesCardsViewToggle';
import UsesCardsOverview from '../UsesCardsOverview/UsesCardsOverview';

interface DevelopmentUsesImportSelectionProps {
    navigateToSection: (section: string) => void;
    hasWriteAccessToProForma: boolean;
    usesMethod: string | null;
    setUsesMethod: (method: string | null) => void;
    unitType: IUnitTypeOption | null;
    setUnitType: Dispatch<SetStateAction<IUnitTypeOption | null>>;
    unitTypeOptions: IUnitTypeOption[];
    signUpMode?: boolean;
}

const DevelopmentUsesImportSelection = ({
    navigateToSection, hasWriteAccessToProForma, usesMethod, setUsesMethod,
    unitType, setUnitType, unitTypeOptions, signUpMode = false,
}: DevelopmentUsesImportSelectionProps) => {
    const {
        uses,
        proForma,
        handleCreateDefaultBudgetClassifications,
        handleStartDevelopmentWithBOEEstimates,
        isDefaultBudgetClassificationsLoading,
        shouldUseProjectActuals,
        handleCreateRecommendedCategories,
        isRecommendedCategoriesLoading,
        handleCopyBudgetCategorizations,
    } = useProFormaSocketContext();

    const [selectedProjectId, setSelectedProjectId] = useState<number>();
    const [budgetEscalation, setBudgetEscalation] = useState('');
    const [searchValue, setSearchValue] = useState('');
    const [view, setView] = useState<VIEW_ENTITY>(VIEW_ENTITY.DEALS);
    const debouncedSearchValue = useDebounce(searchValue, 500);
    const [selectedScenario, setSelectedScenario] = useState<IOptionAnyValue | null>(null);

    const { selectedOrganizationId } = useAuthContext();

    const [proFormaPageLocalStorage] = useLocalStorage<IProFormaPageLocalStorage>(PageSpecificKeys.ProFormaPage, {});
    const currentProFormaLocalStorage = proForma.id in proFormaPageLocalStorage ? proFormaPageLocalStorage[proForma.id] : {} as IProFormaLocalStorage;

    const [usesView, setUsesView] = useState<UsesView>('cards');

    const existingUnitType
        = !isEmpty(currentProFormaLocalStorage) && unitTypeOptions.length
            ? unitTypeOptions.filter(option => option.value === currentProFormaLocalStorage.developmentUnitType)[0]
            : unitTypeOptions?.[0];

    const startUsesFromScratch = () => {
        setUsesMethod(POPULATE_USES_METHOD.START_FROM_SCRATCH.title);
    };

    const handleStartFromScratch = () => {
        if (!uses?.uses?.length) {
            handleCreateDefaultBudgetClassifications(startUsesFromScratch);
        } else {
            setUsesMethod(POPULATE_USES_METHOD.START_FROM_SCRATCH.title);
        }
    };

    const { data: dealsWithProFormas = [], isLoading: isDealsLoading } = useGetDealsWithProFormas({
        orgId: selectedOrganizationId!,
        enabled: Boolean(selectedOrganizationId),
        dealName: debouncedSearchValue,
        statusId: PRO_FORMA_STATUS.SUBSTANTIALLY_COMPLETE,
    });

    const { data: organizationSubprojects = [], isLoading: isProjectsLoading } = useOrganizationSubprojects({
        orgId: selectedOrganizationId!,
        searchValue: debouncedSearchValue,
        enabled: usesMethod === POPULATE_USES_METHOD.IMPORT_FROM_PREVIOUS_PROJECT.title
            || (!isDealsLoading && dealsWithProFormas.length === 0),
    });

    const { data: dealsWithScenarios } = useGetBackOfEnvelopeDeals({
        orgId: selectedOrganizationId!,
        enabled: !signUpMode,
    });

    const { data: scenariosData } = useGetBackOfEnvelopeScenarios({
        orgId: selectedOrganizationId!,
        enabled: !signUpMode,
    });

    const { data: fetchedDeal } = useGetDealById({
        orgId: selectedOrganizationId!,
        dealId: proForma.dealId,
        shouldFetch: !!proForma.dealId,
    });

    const constructionType = fetchedDeal?.constructionType?.name;

    const combinedScenarios = useMemo(() => {
        if (!scenariosData?.items && !dealsWithScenarios?.items) return [];

        const simplifiedScenarios = convertToSimplifiedScenario(dealsWithScenarios?.items || null);

        return [
            ...(simplifiedScenarios || []),
            ...(scenariosData?.items.map(item => ({
                name: item.name,
                id: item.id,
            })) || []),
        ];
    }, [scenariosData?.items, dealsWithScenarios?.items]);

    function convertToSimplifiedScenario(data: any[] | null) {
        if (!data) return null;

        return data.map(item => ({
            name: item.name,
            id: item.id,
            scenarios: [{ id: -1, name: 'default' }],
            hasNestedScenarios: true,
        }));
    }

    const handleSelectScenario = (option: IOptionAnyValue) => {
        if (!option || option.label === 'SEARCH' || option.label === 'CREATE' || option.label === 'BACK') {
            return;
        }

        setSelectedScenario(option);
    };

    const handleCreateScenario = (option: IOptionAnyValue) => {
        // Navigate to BOE page to create new scenario
        navigateToSection('back-of-envelope');
    };

    const handleProceedWithScenario = useCallback(() => {
        if (!selectedScenario) return;

        handleStartDevelopmentWithBOEEstimates(selectedScenario.id?.toString() ?? '', startUsesFromScratch);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedScenario]);

    if (!proForma) {
        // show loading screen instead
        return null;
    }

    const handleProjectSelect = (projectId: number) => {
        setSelectedProjectId(projectId);
    };

    const handleDealSelect = (dealId: number) => {
        console.log('Selected deal:', dealId);
    };

    const handleProFormaSelect = (proFormaId: number) => {
        console.log('Selected pro forma:', proFormaId);
    };

    const handleBudgetEscalationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setBudgetEscalation(e.target.value);
    };

    const handleCopyBudgetCategorizationsFromProForma = (dealId: number, proFormaId: number) => {
        handleCopyBudgetCategorizations(dealId, proFormaId, () => {
            setUsesMethod(POPULATE_USES_METHOD.START_FROM_SCRATCH.title);
        });
    };

    // eslint-disable-next-line react-hooks/rules-of-hooks
    useEffect(() => {
        if (!unitType && existingUnitType) {
            setUnitType(existingUnitType);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [unitType, existingUnitType]);

    const hasProjectsOrDeals = organizationSubprojects.length > 0 || dealsWithProFormas.length > 0;

    return (
        <>
            <RenderIf isTrue={!uses?.uses?.length && !shouldUseProjectActuals && usesMethod === null}>
                <Row className="m-3">
                    <Col m={12} xl={6} className="px-0">
                        <div className="h3 mt-0 text-dark mb-3">
                            Select a method to populate
                            {' '}
                            <b>Uses</b>
                            :
                        </div>
                        <NSCard
                            className="NSCard--level-4 mb-2 p-2 cursor--pointer heap-deal-pro-forma-start-from-previous"
                            onClick={() => hasProjectsOrDeals && setUsesMethod(POPULATE_USES_METHOD.IMPORT_FROM_PREVIOUS_PROJECT.title)}
                            id="import-from-previous-card"
                        >
                            <div className={`text-dark h5 mt-1 mb-1 font-16 ${!hasProjectsOrDeals ? 'text-muted' : ''}`}>
                                {POPULATE_USES_METHOD.IMPORT_FROM_PREVIOUS_PROJECT.title}
                            </div>
                            <div className={`mb-0 lh-sm ${!hasProjectsOrDeals ? 'text-muted' : ''}`}>
                                {POPULATE_USES_METHOD.IMPORT_FROM_PREVIOUS_PROJECT.subtitle}
                            </div>
                        </NSCard>
                        {!hasProjectsOrDeals && (
                            <UncontrolledTooltip target="import-from-previous-card">
                                No previous deals or projects available.
                            </UncontrolledTooltip>
                        )}

                        <NSCard
                            className={
                                `NSCard--level-4 mb-2 p-2 heap-deal-pro-forma-start-from-BoE ${combinedScenarios.length > 0 ? 'cursor--pointer' : ''
                                }`
                            }
                            onClick={() => combinedScenarios.length > 0 && setUsesMethod(POPULATE_USES_METHOD.START_WITH_BACK_OF_ENVELOPE.title)}
                            id="boe-card"
                        >
                            <div className={`text-dark h5 mt-1 mb-1 font-16 ${combinedScenarios.length === 0 ? 'text-muted' : ''}`}>
                                {POPULATE_USES_METHOD.START_WITH_BACK_OF_ENVELOPE.title}
                            </div>
                            <div className={`mb-0 lh-sm ${combinedScenarios.length === 0 ? 'text-muted' : ''}`}>
                                {POPULATE_USES_METHOD.START_WITH_BACK_OF_ENVELOPE.subtitle}
                            </div>
                        </NSCard>
                        {combinedScenarios.length === 0 && (
                            <UncontrolledTooltip target="boe-card">
                                No saved scenarios available.
                            </UncontrolledTooltip>
                        )}

                        <NSCard
                            className="NSCard--level-4 mb-2 p-2 cursor--pointer heap-deal-pro-forma-start-from-rec-categories"
                            onClick={() => setUsesMethod(POPULATE_USES_METHOD.USE_RECOMMENDED_CATEGORIES.title)}
                        >
                            <div className="text-dark h5 mt-1 mb-1 font-16">{POPULATE_USES_METHOD.USE_RECOMMENDED_CATEGORIES.title}</div>
                            <div className="mb-0 lh-sm">{POPULATE_USES_METHOD.USE_RECOMMENDED_CATEGORIES.subtitle}</div>
                        </NSCard>

                        <div
                            role="presentation"
                            onClick={handleStartFromScratch}
                            className="d-flex mt-3 cursor--pointer heap-deal-pro-forma-start-from-scratch"
                        >
                            <RenderIf isTrue={isDefaultBudgetClassificationsLoading}>
                                <span className="spinner-border spinner-border-sm" style={{ height: '20px', width: '20px' }} />
                            </RenderIf>
                            <RenderIf isTrue={!isDefaultBudgetClassificationsLoading}>
                                <div className="text-primary">{POPULATE_USES_METHOD.START_FROM_SCRATCH.title}</div>
                                <FontAwesomeIcon icon={faArrowRight} className="ml-1 my-auto text-primary" />
                            </RenderIf>
                        </div>
                    </Col>
                </Row>
            </RenderIf>

            <RenderIf isTrue={(uses?.uses?.length && !shouldUseProjectActuals) || usesMethod !== null}>
                <div className="m-3">
                    <RenderIf isTrue={usesMethod === POPULATE_USES_METHOD.IMPORT_FROM_PREVIOUS_PROJECT.title}>
                        <ImportSearchBar
                            onExitImport={() => setUsesMethod(null)}
                            searchValue={searchValue}
                            setSearchValue={setSearchValue}
                            onToggleView={view => setView(view)}
                            view={view}
                        />
                        <ImportSourceList
                            organizationSubprojects={organizationSubprojects}
                            deals={dealsWithProFormas}
                            selectedProjectId={selectedProjectId}
                            onProjectSelect={handleProjectSelect}
                            onDealSelect={handleDealSelect}
                            onProFormaSelect={handleProFormaSelect}
                            onCopyBudgetCategorizations={handleCopyBudgetCategorizationsFromProForma}
                            budgetEscalation={budgetEscalation}
                            handleBudgetEscalationChange={handleBudgetEscalationChange}
                            searchValue={debouncedSearchValue}
                            isProjectsLoading={isProjectsLoading}
                            isDealsLoading={isDealsLoading}
                            view={view}
                            setUsesMethod={setUsesMethod}
                        />
                    </RenderIf>
                    <RenderIf isTrue={usesMethod === POPULATE_USES_METHOD.START_WITH_BACK_OF_ENVELOPE.title}>
                        <div className="d-flex flex-column">
                            <div className="d-flex align-items-center mb-2">
                                <div
                                    role="presentation"
                                    onClick={() => {
                                        setUsesMethod(null);
                                        setSelectedScenario(null);
                                    }}
                                    className="d-flex align-items-center cursor--pointer text-primary"
                                >
                                    <FontAwesomeIcon icon={faChevronLeft} className="px-1 font-10" />
                                    <span>Back</span>
                                </div>
                            </div>
                            <div className="d-flex flex-column">
                                <span className="text-white font-20 mb-1">Select a Back-Of-Envelope scenario:</span>
                                <DealScenarioDropdown
                                    scenarios={combinedScenarios}
                                    handleSelectScenario={handleSelectScenario}
                                    handleCreateScenario={handleCreateScenario}
                                    includeSearch
                                    includeLabels
                                    preSelectScenario={selectedScenario || undefined}
                                />
                                <div className="mt-3">
                                    <NSButton
                                        text="Import"
                                        color="primary"
                                        callback={handleProceedWithScenario}
                                        disabled={!selectedScenario}
                                        icon={faAngleRight}
                                        iconPosition="right"
                                        isLoading={isDefaultBudgetClassificationsLoading}
                                    />
                                </div>
                            </div>
                        </div>
                    </RenderIf>

                    <RenderIf isTrue={usesMethod === POPULATE_USES_METHOD.USE_RECOMMENDED_CATEGORIES.title}>
                        <PreviewImportSection
                            title="Previewing import: Recommended categories"
                            subtitle={(
                                <>
                                    Once imported, you can edit any cells with
                                    <span className="text-primary"> blue </span>
                                    text.
                                </>
                            )}
                            onCancel={() => setUsesMethod(null)}
                            onImport={() => {
                                handleCreateRecommendedCategories(() => {
                                    setUsesMethod(POPULATE_USES_METHOD.START_FROM_SCRATCH.title);
                                });
                            }}
                            isLoading={isRecommendedCategoriesLoading}
                            navigateToSection={navigateToSection}
                            initialExpandedState={
                                usesMethod === POPULATE_USES_METHOD.USE_RECOMMENDED_CATEGORIES.title
                                    ? RECOMMENDED_CATEGORIES_EXPANDED_STATE[constructionType || 'Ground Up']
                                    : undefined
                            }
                            previewData={RECOMMENDED_CATEGORIES_PREVIEW_DATA[constructionType || 'Ground Up']}
                            unitTypeOptions={unitTypeOptions}
                            setUnitType={setUnitType}
                            proForma={proForma}
                        />
                    </RenderIf>

                    <RenderIf
                        isTrue={(usesMethod === POPULATE_USES_METHOD.START_FROM_SCRATCH.title || uses?.uses?.length) && unitType && proForma}
                    >
                        <div className='mb-3'>
                            <UsesCardsViewToggle value={usesView} onChange={setUsesView} />
                        </div>
                        {usesView === 'cards' ? (
                            <UsesCardsOverview
                                uses={uses!}
                                proForma={proForma}
                                hasWriteAccessToProForma={hasWriteAccessToProForma}
                            />
                        ) : (
                            <UsesTableProvider
                                navigateToSection={navigateToSection}
                                isProFormaReadOnly={!hasWriteAccessToProForma || !proForma.builtGrossSquareFootage}
                            >
                                <DevelopmentUsesTable
                                    uses={uses || {}}
                                    unitType={unitType!}
                                    setUnitType={setUnitType}
                                    unitTypeOptions={unitTypeOptions}
                                    proForma={proForma}
                                />
                            </UsesTableProvider>
                        )}
                    </RenderIf>

                </div>
            </RenderIf>
        </>

    );
};

export default DevelopmentUsesImportSelection;
